/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */
import { useEffect, useState } from 'react';
import { StyledProvisioningPageHeading } from '../../provisioning/StyledProvisioningPageHeading';
import { Box, Button, Divider, Typography } from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import { shallowEqual, useSelector } from 'react-redux';
import { BeamSelect } from '../../common/formik/BeamSelect';
import appTheme from '../../common/theme/appTheme.json';
import * as yup from 'yup';
import { HnaNetwork, HnaRequest, HnaRequestStatus, HnaZone } from '../../../shared/entities/hnaRequest';

import { ajaxRaw } from '../../../utils/StoreUtils';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';

import { makeStyles } from '@mui/styles';
import { BeamInputLabel } from '../../common/formik/BeamInputLabel';
import { FlexDuoWrapper, FlexFullWrapper, FlexSoloWrapper, HnaRequestHeader } from './HnaRequestsForm';
import { HnaZoneViewer } from './HnaZoneViewer';
import { selectHnarequestEmail, selectUserData } from '../../../store';
import { areAllowedRolesPresent } from '../../../utils/RBACUtils';
import { Authorities } from '../../../shared/entities/user';
import { BeamSubmitButton } from '../../common/formik/BeamSubmitButton';
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from 'formik';
import { BeamDatePicker } from '../../common/formik/BeamDatePicker';
import { HnaZoneSelector } from './HnaZoneSelector';
import { formatDottedDateYYYYMMDD } from '../../../utils/DateTimeUtils';
import BeamTooltip from '../../common/beam/BeamTooltip';
import BeamSectionAlert from '../../common/beam/BeamSectionAlert';
import HnaZoneLegend from './HnaZoneLegend';
import HnaRequestToClipboardButton from './HnaRequestToClipboardButton';
import { isEqual, isNil } from 'lodash';
import { BeamInput } from "../../common/formik/BeamInput";

const useStyles = makeStyles({
  disclaimer: {
    color: appTheme.primaryTextColor,
    fontFamily: appTheme.appFontFamily,
    marginTop: '5px',
    marginBottom: '10px',
  },
  requestInfo: {
    color: appTheme.primaryTextColor,
    fontFamily: appTheme.appFontFamily,
    marginTop: '10px',
    marginBottom: '20px',
  },
  sectionHeaderStyle: {
    fontFamily: 'Uni Neue Light',
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontSize: '20px',
    lineHeight: '28px',
    letterSpacing: 0.2,
    color: '#FFFFFF',
  },
});

const HnaRequestView = () => {
  const hnaRequestEmail = useSelector(selectHnarequestEmail, shallowEqual);
  const [submissionError, setError] = useState<string | undefined>(undefined);
  const classes = useStyles();
  const userData = useSelector(selectUserData, shallowEqual);
  const isApprover = areAllowedRolesPresent(userData, Authorities.ROLE_HNA_APPROVER);
  let { uuid } = useParams();
  const statusOptions = [HnaRequestStatus.REQUESTED, HnaRequestStatus.PROCESSING, HnaRequestStatus.COMPLETE];
  
  const history = useHistory();
  const [initialFormState, setInitialFormState] = useState<HnaRequest>({
    id: '',
    requestorName: '',
    requestorOrgName: '',
    antennaType: '',
    missionNumber: '',
    tailNumbers: [],
    missionStartDate: '',
    missionEndDate: '',
    requestedAt: '',
    apacsId: '',
    additionalInfo: '',
    hnaZones: [],
    status: HnaRequestStatus.REQUESTED,
  });

  const [retrievedZones, setRetrievedZones] = useState<HnaZone[]>([]);

  // Request initial data
  useEffect(() => {
    if (initialFormState.id === '') {
      ajaxRaw.get(`/mpt/hna-requests/${uuid}`).subscribe({
        next: value => {
          setInitialFormState({ ...initialFormState, ...value.response });
          
          // The code below makes a shallow copy for retrievedZones that doesn't update again.
          // Setting it directly to value.response causes it to mutate for unknown reasons.
          let hnaZones = [] as HnaZone[];
          value.response.hnaZones.forEach((zone: HnaZone) => {
            let nets = [] as HnaNetwork[];
            zone.networks.forEach((n: HnaNetwork) => {
              nets.push({
                network: n.network,
                state: n.state,
              });
            });
            hnaZones.push({ country: zone.country, networks: nets });
          });
          setRetrievedZones(hnaZones);
        },
        error: err => setError(err.message),
      });
    }
  }, [isApprover, uuid, initialFormState]);

  const handleHnaZoneChange = (values: HnaRequest) => {
    if (values.status === HnaRequestStatus.REQUESTED) {
      if (
        retrievedZones.length !== 0 &&
        !isEqual(values.hnaZones, retrievedZones)
      ) {
        values.status = HnaRequestStatus.PROCESSING;
      }
    }
    return () => {}
  };

  const canEdit = isApprover || initialFormState.status === HnaRequestStatus.REQUESTED;
  return (
    <>
      <HnaRequestHeader
        title={'View HNA Request'}
        editButton={canEdit}
        handleEdit={() => history.push(`/plans/hna-requests/edit/` + uuid)}
      />
      <div style={{ width: '830px', margin: '0 auto' }}>
        <Box style={{ width: '800px', paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <StyledProvisioningPageHeading>HNA Request</StyledProvisioningPageHeading>
          {!isApprover ? (
            <Box display="flex" justifyContent="space-between" flexWrap="wrap" rowGap="20px" columnGap={'20px'}>
              {!canEdit && (
                <BeamSectionAlert style={{ width: '100%' }}>
                  <div style={{ fontSize: 14 }}>
                    This request{' '}
                    {initialFormState.status === HnaRequestStatus.PROCESSING
                      ? 'is currently being'
                      : 'has already been'}{' '}
                    processed and cannot be edited.
                    <div style={{ marginTop: '8px' }} />
                    To request changes, please email{' '}
                    <a style={{ color: '#009DE0' }} href={'mailto:' + hnaRequestEmail}>
                      {hnaRequestEmail}
                    </a>
                  </div>
                </BeamSectionAlert>
              )}
              <FlexSoloWrapper>
                <BeamInputLabel htmlFor={'status'} label={'Authorization Status'} />
                <Box className={classes.requestInfo}>
                  {initialFormState
                    ? initialFormState.status.charAt(0) + initialFormState.status.slice(1).toLowerCase()
                    : undefined}
                </Box>
              </FlexSoloWrapper>
              {!isNil(initialFormState?.authorizationStartDate) && !isNil(initialFormState?.authorizationEndDate) && (
                <>
                  <FlexDuoWrapper>
                    <BeamInputLabel htmlFor={'authorizationStartDate'} label={'Authorization Start Date'} />
                    <Box className={classes.requestInfo}>
                      {formatDottedDateYYYYMMDD(initialFormState?.authorizationStartDate ?? '')}
                    </Box>
                  </FlexDuoWrapper>
                  <FlexDuoWrapper>
                    <BeamInputLabel htmlFor={'authorizationEndDate'} label={'Authorization End Date'} />
                    <Box className={classes.requestInfo}>
                      {formatDottedDateYYYYMMDD(initialFormState?.authorizationEndDate ?? '')}
                    </Box>
                  </FlexDuoWrapper>
                </>
              )}
            </Box>
          ) : (
            <Formik
              enableReinitialize={true}
              initialValues={initialFormState}
              onSubmit={(values, { setSubmitting }: FormikHelpers<HnaRequest>) => {
                setError(undefined);
                setSubmitting(true);

                let url = `/mpt/hna-requests/${values.id}`;
                let operation = ajaxRaw.put;

                if (values.status !== HnaRequestStatus.COMPLETE) {
                  delete values.authorizationStartDate;
                  delete values.authorizationEndDate;
                }

                operation(url, JSON.stringify(values)).subscribe({
                  next: (response: AjaxResponse<any>) => {
                    if (response.status !== 200 && response.status !== 201) {
                      setError(JSON.stringify(response.response));
                    }
                    history.push(`/plans/hna-requests`);
                    setSubmitting(false);
                  },
                  error: (err: AjaxError) => {
                    setError(err.response.message ? err.response.message : err.message);
                    setSubmitting(false);
                  },
                });
              }}
              validationSchema={yup.object().shape({
                authorizationStartDate: yup
                  .date()
                  .typeError('Please select authorization start date.')
                  .nullable()
                  .when('status', {
                    is: HnaRequestStatus.COMPLETE,
                    then: schema => schema.required('Please select authorization start date.'),
                  }),
                authorizationEndDate: yup
                  .date()
                  .typeError('Please select authorization end date.')
                  .nullable()
                  .when('status', {
                    is: HnaRequestStatus.COMPLETE,
                    then: schema =>
                      schema
                        .required('Please select authorization end date.')
                        .min(yup.ref('authorizationStartDate'), "End date can't be before start date."),
                  }),
              })}
            >
              {({ isSubmitting, dirty, values }) => (
                <Form
                  style={{
                    width: '800px',
                    display: 'flex',
                    flexDirection: 'column',
                    rowGap: '15px',
                  }}
                >
                  {submissionError ? <ErrorMessage name={submissionError} /> : <></>}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    rowGap="20px"
                    columnGap={'20px'}
                    marginBottom={'20px'}
                  >
                    <FlexSoloWrapper>
                      <BeamSelect options={statusOptions} name={'status'} label={'Request Status'} />
                    </FlexSoloWrapper>
                    <BeamTooltip
                      title={"Request status must be 'Complete' before setting authorization dates"}
                      placement={'top'}
                      disableHoverListener={values.status === HnaRequestStatus.COMPLETE}
                      disableFocusListener={values.status === HnaRequestStatus.COMPLETE}
                    >
                      <FlexDuoWrapper>
                        <BeamDatePicker
                          name={'authorizationStartDate'}
                          label={'Authorization Start Date'}
                          required={true}
                          disableInput={values.status !== HnaRequestStatus.COMPLETE}
                        />
                      </FlexDuoWrapper>
                    </BeamTooltip>
                    <BeamTooltip
                      title={"Request status must be 'Complete' before setting authorization dates"}
                      placement={'top'}
                      disableHoverListener={values.status === HnaRequestStatus.COMPLETE}
                      disableFocusListener={values.status === HnaRequestStatus.COMPLETE}
                    >
                      <FlexDuoWrapper>
                        <BeamDatePicker
                          name={'authorizationEndDate'}
                          label={'Authorization End Date'}
                          required={true}
                          disableInput={values.status !== HnaRequestStatus.COMPLETE}
                        />
                      </FlexDuoWrapper>
                    </BeamTooltip>
                    <BeamInput label={"Notes"} name={"notes"} multiline={true} sx={{ flex: 1 }} />
                  </Box>
                  <Typography className={classes.sectionHeaderStyle}>Network information</Typography>
                  <Field
                    onChange={handleHnaZoneChange(values)}
                    name="hnaZones"
                    component={HnaZoneSelector}
                    triState={true}
                    countries={[]}
                  />
                  <HnaZoneLegend />
                  <Box display="flex" alignItems={'center'} justifyContent={'center'} columnGap={'50px'}>
                    <Button
                      variant="contained"
                      size="large"
                      disabled={isSubmitting}
                      sx={{ backgroundColor: '#141D24', height: '40px', width: '210px' }}
                      onClick={() => {
                        history.push(`/plans/hna-requests`);
                      }}
                    >
                      Cancel
                    </Button>
                    <BeamSubmitButton text={'Save Approvals'} width="210px" />
                  </Box>
                </Form>
              )}
            </Formik>
          )}
          <Divider sx={{ bgcolor: 'white' }} />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <StyledProvisioningPageHeading>HNA Request Details</StyledProvisioningPageHeading>
            <HnaRequestToClipboardButton hnaRequest={initialFormState} />
          </div>
          <StyledProvisioningPageHeading>Requestor information</StyledProvisioningPageHeading>
          <Box display="flex" justifyContent="space-between" columnGap={'30px'}>
            <FlexDuoWrapper>
              <BeamInputLabel htmlFor={'requestorName'} label={'Full Name'} />
              <Box className={classes.requestInfo}>{initialFormState?.requestorName}</Box>
            </FlexDuoWrapper>
            <FlexDuoWrapper>
              <BeamInputLabel htmlFor={'requestorOrgName'} label={'Organization/Squadron'} />
              <Box className={classes.requestInfo}>{initialFormState?.requestorOrgName}</Box>
            </FlexDuoWrapper>
          </Box>
          <StyledProvisioningPageHeading> Mission information </StyledProvisioningPageHeading>
          <p className={classes.disclaimer}>Note: Available tail numbers are based on your mission’s antenna type.</p>
          <Box display="flex" justifyContent="space-between" flexWrap="wrap" rowGap="20px" columnGap={'20px'}>
            <FlexSoloWrapper>
              <BeamInputLabel htmlFor={'antennaType'} label={'Antenna Type'} />
              <Box className={classes.requestInfo}>{initialFormState?.antennaType}</Box>
            </FlexSoloWrapper>
            <FlexDuoWrapper>
              <BeamInputLabel htmlFor={'missionNumber'} label={'Mission Number'} />
              <Box className={classes.requestInfo}>{initialFormState?.missionNumber}</Box>
            </FlexDuoWrapper>
            <FlexDuoWrapper>
              <BeamInputLabel htmlFor={'tailNumbers'} label={'Tail number(s)'} />
              <Box className={classes.requestInfo}>{initialFormState?.tailNumbers.join(', ')}</Box>
            </FlexDuoWrapper>
            <FlexDuoWrapper>
              <BeamInputLabel htmlFor={'missionStartDate'} label={'Mission Start Date'} />
              <Box className={classes.requestInfo}>{formatDottedDateYYYYMMDD(initialFormState?.missionStartDate)}</Box>
            </FlexDuoWrapper>
            <FlexDuoWrapper>
              <BeamInputLabel htmlFor={'missionEndDate'} label={'Mission End Date'} />
              <Box className={classes.requestInfo}>{formatDottedDateYYYYMMDD(initialFormState?.missionEndDate)}</Box>
            </FlexDuoWrapper>
            <FlexSoloWrapper>
              <BeamInputLabel htmlFor={'apacsId'} label={'APACS'} />
              <Box className={classes.requestInfo}>{initialFormState?.apacsId}</Box>
            </FlexSoloWrapper>
            <FlexFullWrapper>
              <BeamInputLabel htmlFor={'additionalInfo'} label={'Additional Information (optional)'} />
              <Box className={classes.requestInfo}>{initialFormState?.additionalInfo}</Box>
            </FlexFullWrapper>
          </Box>
          {isApprover ? (
            <></>
          ) : (
            <div style={{ marginBottom: '32px' }}>
              <StyledProvisioningPageHeading>Network information</StyledProvisioningPageHeading>
              <HnaZoneViewer readOnly={true} hnaZones={initialFormState?.hnaZones} />
              <Box marginTop={'16px'}>
                <HnaZoneLegend />
              </Box>
            </div>
          )}
        </Box>
      </div>
    </>
  );
};

export default HnaRequestView;
